/**
 * V13.17 — Volet Coach affiché sur la fiche Athlète (identité unifiée).
 *
 * Principe : UNE PERSONNE → UNE IDENTITÉ → PLUSIEURS RÔLES.
 * Ne jamais créer de doublon Coach quand un Athlete existe.
 *
 * Alimenté par GET /api/athletes/:slug/coach-panel qui unifie :
 *   - athletes.also_offers_coaching (V13.2) : coach virtuel
 *   - coaches document canonique (owner_user_id partagé) : legacy V13.1
 *
 * IMPORTANT : la liste des athlètes coachés provient des vrais EntityLink
 * (`role=athlete_coached_by, status=accepted`). Ne jamais maintenir cette
 * liste manuellement.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { UserCheck, Users, Award, Building2, Calendar } from "lucide-react";
import api from "@/lib/api";

export function CoachPanel({ slug, dense = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/athletes/${slug}/coach-panel`);
        if (!cancelled) setData(d);
      } catch {
        if (!cancelled) setData(null);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return <div className="space-y-2"><Skeleton className="h-24 w-full" /><Skeleton className="h-32 w-full" /></div>;
  }

  if (!data || !data.is_coach) {
    return (
      <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="coach-panel-empty">
        Cette personne n'est pas déclarée comme coach sur AITO.PF.
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="coach-panel">
      {/* Bloc principal */}
      <Card className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="h-10 w-10 rounded-full bg-[hsl(var(--aito-lagoon))]/15 flex items-center justify-center border border-[hsl(var(--aito-lagoon))]/30 shrink-0">
            <UserCheck className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
          </div>
          <div className="min-w-0">
            <h3 className="font-display font-semibold text-base">Volet Coach</h3>
            <p className="text-xs text-muted-foreground">
              Rôle secondaire de cette personne. Toutes les informations proviennent
              des liens réels enregistrés sur AITO.PF.
            </p>
          </div>
        </div>

        {data.coach_bio && (
          <p className="text-sm text-foreground mt-2 whitespace-pre-line" data-testid="coach-panel-bio">
            {data.coach_bio}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          {/* Disciplines enseignées */}
          {data.disciplines_taught?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1 flex items-center gap-1">
                <Award className="h-3 w-3" />Disciplines enseignées
              </div>
              <div className="flex flex-wrap gap-1">
                {data.disciplines_taught.map((d) => (
                  <Badge key={d} variant="outline" className="capitalize text-[11px]" data-testid={`coach-disc-${d}`}>{d}</Badge>
                ))}
              </div>
            </div>
          )}

          {/* Qualifications */}
          {data.qualifications?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1 flex items-center gap-1">
                <Award className="h-3 w-3" />Qualifications
              </div>
              <ul className="text-xs space-y-0.5">
                {data.qualifications.map((q, i) => (
                  <li key={i} className="text-foreground/80">• {q}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Année de début */}
          {data.year_started && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1 flex items-center gap-1">
                <Calendar className="h-3 w-3" />Coache depuis
              </div>
              <div className="text-sm font-medium">{data.year_started}</div>
            </div>
          )}

          {/* Clubs */}
          {data.clubs?.length > 0 && (
            <div className="sm:col-span-2">
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1 flex items-center gap-1">
                <Building2 className="h-3 w-3" />Club(s) où il/elle coache
              </div>
              <div className="flex flex-wrap gap-1.5">
                {data.clubs.map((c) => (
                  <Link key={c.id} to={`/clubs/${c.slug || c.id}`} className="focus-ring rounded">
                    <Badge variant="secondary" className="hover:bg-[hsl(var(--aito-lagoon))]/20 transition text-[11px]" data-testid={`coach-club-${c.slug || c.id}`}>
                      {c.logo_url && <img src={c.logo_url} alt="" className="h-3.5 w-3.5 rounded mr-1 object-cover" />}
                      {c.name}
                    </Badge>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {(!data.coach_bio &&
          !data.disciplines_taught?.length &&
          !data.qualifications?.length &&
          !data.clubs?.length &&
          !data.year_started) && (
          <p className="text-xs text-muted-foreground italic">
            Aucune information coach renseignée pour l'instant.
          </p>
        )}
      </Card>

      {/* Athlètes coachés */}
      <Card className="p-5" data-testid="coach-panel-coached">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h4 className="font-display font-semibold text-sm flex items-center gap-1.5">
            <Users className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
            Athlètes coaché·e·s ({data.coached_count})
          </h4>
          <span className="text-[10px] text-muted-foreground italic">
            Issus des liens Athlete ↔ Coach validés
          </span>
        </div>
        {!data.coached_athletes?.length ? (
          <p className="text-sm text-muted-foreground italic">
            Aucun athlète relié pour l'instant. Un athlète peut déclarer ce coach depuis sa fiche.
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {data.coached_athletes.map((a) => (
              <Link
                key={a.id}
                to={`/athletes/${a.slug || a.id}`}
                className="flex items-center gap-2 p-2 rounded-md border hover:bg-muted/40 focus-ring transition"
                data-testid={`coach-panel-athlete-${a.slug || a.id}`}
              >
                <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center overflow-hidden shrink-0 border">
                  {a.photo_url
                    ? <img src={a.photo_url} className="w-full h-full object-cover" alt="" />
                    : <span className="text-xs font-semibold">{(a.full_name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</span>}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium truncate">{a.full_name}</div>
                  {a.main_discipline && (
                    <div className="text-[10px] text-muted-foreground capitalize truncate">{a.main_discipline}</div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
